import React, { useState, useCallback, useEffect } from 'react';
import {
  LayoutDashboard, Users, Building2, Shield, FileText, Lock, Settings,
  Receipt, Coins, ClipboardList, Map, Package, ScrollText, CheckSquare, Menu,
  LogOut, Gem, ChevronRight, Database, Settings2,
} from 'lucide-react';
import api, { getStoredRole, getStoredUser } from '../api';
import DashboardScreen from './AdminDashboardScreen';
import UserManagement from './UserManagement';
import BranchManagementScreen from './BranchManagementScreen';
import AuditDashboard from './AuditDashboard';
import DetailedAuditLogs from './DetailedAuditLogs';
import UserRoleMatrix from './UserRoleMatrix';
import MySecurity from './MySecurity';
import SystemConfiguration from './SystemConfiguration';
import InvoiceApprovalQueue from '../Accounts/InvoiceApprovalQueue';
import MetalExchangeAdminControl from './MetalExchangeAdminControl';
import DesignComplexitySettings from './DesignComplexitySettings';
import QuotationApprovals from './QuotationApprovals';
import QCChecklistBuilder from './QCChecklistBuilder';
import StaffOverview from './StaffOverview';
import StateMasterSettings from './StateMasterSettings';
import DispatchApprovals from './DispatchApprovals';
import MasterDataManagerView from './MasterDataManager';
import ProductionMasterSetupView from './ProductionMasterSetup';
import AdminGoalsPage from '../Goals/AdminGoalsPage';
import CustomerCreditLimits from './CustomerCreditLimits';
import SignatureUpload from '../Admin/SignatureUpload';

const ADMIN_STYLES = `
  .admin-layout { display: flex; height: 100vh; overflow: hidden; font-family: 'Inter', sans-serif; }
  .admin-sidebar {
    width: 260px; min-width: 260px; background: linear-gradient(180deg,#1e1b4b 0%,#312e81 50%,#1e293b 100%);
    color: white; display: flex; flex-direction: column; transition: transform .3s ease; z-index: 20;
    box-shadow: 4px 0 24px rgba(0,0,0,.3);
  }
  .admin-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; background: #f8fafc; }
  .admin-header {
    height: 64px; background: white; border-bottom: 1px solid #e2e8f0;
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 24px; box-shadow: 0 1px 4px rgba(0,0,0,.06); z-index: 10; flex-shrink: 0;
  }
  .admin-content {
    flex: 1; overflow-y: auto; padding: 24px; background: #f8fafc;
    background-image: radial-gradient(circle at 100% 0%, rgba(99,102,241,.08) 0, transparent 28%), radial-gradient(circle at 0% 100%, rgba(148,163,184,.08) 0, transparent 24%);
  }
  .admin-content,
  .admin-content h1, .admin-content h2, .admin-content h3, .admin-content h4, .admin-content h5, .admin-content h6,
  .admin-content p, .admin-content span, .admin-content label, .admin-content small,
  .admin-content td, .admin-content th, .admin-content li, .admin-content a,
  .admin-content input, .admin-content textarea, .admin-content select {
    color: #000 !important;
  }
  .admin-content input::placeholder,
  .admin-content textarea::placeholder { color: #000 !important; opacity: 1; }
  .admin-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 19; display: none; }
  .admin-overlay.visible { display: block; }
  .admin-nav-scroll { padding: 16px 12px; display: flex; flex-direction: column; gap: 4px; flex: 1; overflow-y: auto; }
  .admin-nav-label { font-size: 10px; font-weight: 700; color: #6366f1; text-transform: uppercase; letter-spacing: 1px; padding: 0 4px 8px; }
  .admin-nav-item {
    display: flex; align-items: center; gap: 12px; padding: 10px 16px; border-radius: 10px;
    cursor: pointer; transition: all .2s; border: none; background: transparent; color: #a5b4fc;
    font-size: 14px; font-weight: 500; width: 100%; text-align: left;
  }
  .admin-nav-item:hover { background: rgba(255,255,255,.08); color: white; }
  .admin-nav-item.active { background: rgba(255,255,255,.12); color: white; box-shadow: inset 0 0 0 1px rgba(255,255,255,.1); }
  .admin-badge {
    min-width: 20px; height: 20px; border-radius: 999px; padding: 0 6px;
    display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700;
    background: rgba(255,255,255,.14); color: white;
  }
  @media(max-width:768px) {
    .admin-sidebar { position: fixed; height: 100%; transform: translateX(-100%); }
    .admin-sidebar.open { transform: translateX(0); }
    .admin-content { padding: 16px; }
  }
  @media(min-width:769px) { .admin-sidebar { position: relative; transform: none !important; } }
`;

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'users', label: 'User Management', icon: Users },
  { id: 'branches', label: 'Branches', icon: Building2 },
  { id: 'staff', label: 'Staff Overview', icon: Users },
  { id: 'dispatch_approvals', label: 'Dispatch Approvals', icon: Package },
  { id: 'signatures', label: 'Signatures', icon: FileText },
  { id: 'quotation_approvals', label: 'Quotation Approvals', icon: FileText, badgeKey: 'quotationApprovalCount' },
  { id: 'invoice_approvals', label: 'Invoice Approvals', icon: Receipt },
  { id: 'roles', label: 'Role Matrix', icon: Shield },
  { id: 'audit', label: 'Audit Dashboard', icon: ClipboardList },
  { id: 'audit_logs', label: 'Audit Logs', icon: ScrollText },
  { id: 'design_complexity', label: 'Design Complexity', icon: Gem },
  { id: 'qc_checklists', label: 'QC Checklists', icon: CheckSquare },
  { id: 'master_data', label: 'Master Data', icon: Database },
  { id: 'production_setup', label: 'Production Setup', icon: Settings2 },
  { id: 'goals', label: 'Goals & Tasks', icon: CheckSquare },
  { id: 'customer_credit_limits', label: 'Customer Credit Limits', icon: Users },
  { id: 'metal_exchange', label: 'Metal Exchange', icon: Coins },
  { id: 'state_master', label: 'State Master', icon: Map },
  { id: 'system_config', label: 'System Config', icon: Settings },
  { id: 'security', label: 'My Security', icon: Lock },
];

function asText(value) {
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (value && typeof value === 'object') {
    return String(
      value.display_name ||
      value.name ||
      value.username ||
      value.role ||
      value.user_role ||
      value.message ||
      value.code ||
      ''
    ).trim();
  }
  return '';
}

function AdminDashboard({ onLogout }) {
  const [currentView, setCurrentView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [quotationApprovalCount, setQuotationApprovalCount] = useState(0);

  const navigate = useCallback((screenId) => {
    setCurrentView(screenId);
    setSidebarOpen(false);
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadBadgeCounts = async () => {
      try {
        const res = await api.get('/admin/dashboard');
        if (!mounted) return;
        const quotationCount = Number(res?.data?.badges?.quotation_approvals ?? res?.data?.sales?.pending_quotations ?? 0);
        setQuotationApprovalCount(Number.isFinite(quotationCount) ? quotationCount : 0);
      } catch {
        if (mounted) {
          setQuotationApprovalCount(0);
        }
      }
    };

    loadBadgeCounts();
    const intervalId = setInterval(loadBadgeCounts, 30000);
    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, []);

  const renderContent = () => {
    switch (currentView) {
      case 'dashboard': return <DashboardScreen />;
      case 'staff': return <StaffOverview />;
      case 'branches': return <BranchManagementScreen />;
      case 'users': return <UserManagement />;
      case 'audit': return <AuditDashboard />;
      case 'audit_logs': return <DetailedAuditLogs />;
      case 'roles': return <UserRoleMatrix />;
      case 'security': return <MySecurity />;
      case 'system_config': return <SystemConfiguration />;
      case 'invoice_approvals': return <InvoiceApprovalQueue />;
      case 'quotation_approvals': return <QuotationApprovals />;
      case 'metal_exchange': return <MetalExchangeAdminControl />;
      case 'dispatch_approvals': return <DispatchApprovals />;
      case 'signatures': return <SignatureUpload />;
      case 'design_complexity': return <DesignComplexitySettings />;
      case 'qc_checklists': return <QCChecklistBuilder />;
      case 'master_data': return <MasterDataManagerView />;
      case 'production_setup': return <ProductionMasterSetupView />;
      case 'goals': return <AdminGoalsPage />;
      case 'customer_credit_limits': return <CustomerCreditLimits />;
      case 'state_master': return <StateMasterSettings />;
      default: return <DashboardScreen />;
    }
  };

  const getPageTitle = () => NAV_ITEMS.find((item) => item.id === currentView)?.label || 'Admin';
  const storedUser = getStoredUser();
  const userName = [
    [storedUser?.first_name, storedUser?.last_name].filter(Boolean).join(' ').trim(),
    asText(storedUser?.display_name),
    asText(storedUser?.name),
    asText(storedUser?.username),
  ].find((value) => asText(value)) || 'Admin';
  const adminRoleLabel = [
    asText(storedUser?.role),
    asText(storedUser?.user_role),
    asText(getStoredRole()),
  ].find((value) => asText(value)) || 'Admin';

  return (
    <>
      <style>{ADMIN_STYLES}</style>
      <div className="admin-layout prod-module-root">
        <div className={`admin-overlay ${sidebarOpen ? 'visible' : ''}`} onClick={() => setSidebarOpen(false)} />

        <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg,#f59e0b,#d97706)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Gem size={22} color="white" />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: '-.5px', color: 'white' }}>
                ZILVERRA<span style={{ color: '#818cf8' }}>JEWELS</span>
              </div>
              <div style={{ fontSize: 11, color: '#a5b4fc' }}>Admin Module</div>
            </div>
          </div>

          <div className="admin-nav-scroll">
            <div className="admin-nav-label">Admin Hub</div>
            {NAV_ITEMS.map((item, index) => {
              const badge = item.badgeKey === 'quotationApprovalCount' ? quotationApprovalCount : null;

              return (
                <button
                  key={item.id}
                  className={`admin-nav-item ${currentView === item.id ? 'active' : ''}`}
                  onClick={() => navigate(item.id)}
                >
                  <item.icon size={18} />
                  {item.label}
                  {typeof badge === 'number' && badge > 0 && <span className="admin-badge">{badge}</span>}
                  {currentView === item.id && <ChevronRight size={14} style={{ marginLeft: 'auto' }} />}
                </button>
              );
            })}
          </div>

          <div style={{ margin: '12px', padding: '12px', background: 'rgba(0,0,0,.25)', borderRadius: 10, border: '1px solid rgba(255,255,255,.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white' }}>
                {userName.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>{userName}</div>
                <div style={{ fontSize: 11, color: '#a5b4fc' }}>{adminRoleLabel}</div>
              </div>
            </div>
          </div>
        </aside>

        <main className="admin-main">
          <header className="admin-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a1a1a', padding: 4, display: 'flex', alignItems: 'center' }}
                onClick={() => setSidebarOpen((prev) => !prev)}
              >
                <Menu size={22} />
              </button>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#000', margin: 0 }}>{getPageTitle()}</h2>
            </div>
            <button
              onClick={() => window.confirm('Logout?') && onLogout?.()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              <LogOut size={16} />
              Logout
            </button>
          </header>

          <div className="admin-content">
            {renderContent()}
          </div>
        </main>
      </div>
    </>
  );
}

export default AdminDashboard;
